import { useRef, useEffect, useMemo, useState } from 'react';
import { SlidersHorizontal, XCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Button,
  Badge,
  Label,
  Text,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@musa.tech/design-system';
import { MultiSelect } from '@/components/dashboard/MultiSelect';
import { EstablishmentMultiSelect } from '@/components/dashboard/EstablishmentMultiSelect';
import { PeriodPicker } from '@/components/indicadores-ambientais/PeriodPicker';
import { useFilterOptions } from '@/hooks/useFilterOptions';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAuthContext } from '@/components/AuthProvider';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { maskEstablishmentName, maskGeneratorName } from '@/utils/demoMode';

interface IAFiltersProps {
  months: string[];
  defaultMonth: string;
  maxMonth?: string;
  establishments: string[];
  generators: string[];
  onMonthsChange: (months: string[]) => void;
  onEstablishmentsChange: (establishments: string[]) => void;
  onGeneratorsChange: (generators: string[]) => void;
  onClear: () => void;
}

function periodChipLabel(months: string[]): string {
  const sorted = [...months].sort().reverse();
  if (sorted.length === 1) return format(parseISO(`${sorted[0]}-01`), 'MMM/yy', { locale: ptBR });
  if (sorted.length === 2)
    return sorted.map((m) => format(parseISO(`${m}-01`), 'MMM/yy', { locale: ptBR })).join(', ');
  return `${sorted.length} meses`;
}

export const IAFilters = ({
  months,
  defaultMonth,
  maxMonth,
  establishments,
  generators,
  onMonthsChange,
  onEstablishmentsChange,
  onGeneratorsChange,
  onClear,
}: IAFiltersProps) => {
  const [open, setOpen] = useState(false);
  const { trackButtonClick } = useAnalytics();
  const [draftMonths, setDraftMonths] = useState<string[]>(months);
  const [draftEstablishments, setDraftEstablishments] = useState<string[]>(establishments);
  const [draftGenerators, setDraftGenerators] = useState<string[]>(generators);

  const { profile } = useAuthContext();
  const { isDemoMode } = useDemoMode();
  const isInternal = profile?.is_internal === true;

  const { establishments: availableEstablishments, generators: availableGenerators } = useFilterOptions({
    selectedGeneratorExternalIds: draftGenerators,
  });

  const showGeneratorFilter = isInternal || availableGenerators.length > 1;

  // Sync drafts when drawer opens
  useEffect(() => {
    if (open) {
      setDraftMonths(months);
      setDraftEstablishments(establishments);
      setDraftGenerators(generators);
    }
  }, [open, months, establishments, generators]);

  // Clear establishments that no longer belong to selected generators
  const prevDraftGeneratorsRef = useRef<string[]>(draftGenerators);
  useEffect(() => {
    const changed =
      prevDraftGeneratorsRef.current.length !== draftGenerators.length ||
      !prevDraftGeneratorsRef.current.every((g) => draftGenerators.includes(g));

    if (!changed) return;
    prevDraftGeneratorsRef.current = draftGenerators;
    if (draftGenerators.length === 0) return;

    const validIds = new Set(availableEstablishments.map((e) => e.establishment_id));
    setDraftEstablishments((prev) => prev.filter((id) => validIds.has(id)));
  }, [draftGenerators, availableEstablishments]);

  const establishmentOptions = useMemo(
    () =>
      availableEstablishments.map((e) => ({
        value: e.establishment_id,
        label: maskEstablishmentName(e.establishment_name, isDemoMode),
      })),
    [availableEstablishments, isDemoMode],
  );

  const generatorOptions = useMemo(
    () =>
      availableGenerators.map((g) => ({
        value: g.generator_id,
        label: maskGeneratorName(g.generator_name, isDemoMode),
      })),
    [availableGenerators, isDemoMode],
  );

  const isDefaultPeriod = months.length === 1 && months[0] === defaultMonth;

  const activeCount = [
    !isDefaultPeriod,
    establishments.length > 0,
    generators.length > 0,
  ].filter(Boolean).length;

  const chips = useMemo(() => {
    const result: Array<{ key: string; label: string; onRemove?: () => void }> = [];

    result.push({
      key: 'period',
      label: `Período: ${periodChipLabel(months)}`,
      onRemove: !isDefaultPeriod ? () => onMonthsChange([defaultMonth]) : undefined,
    });

    generators.forEach((id) => {
      const opt = generatorOptions.find((o) => o.value === id);
      result.push({
        key: `gen-${id}`,
        label: `Gerador: ${opt?.label || id}`,
        onRemove: () => onGeneratorsChange(generators.filter((g) => g !== id)),
      });
    });

    establishments.forEach((id) => {
      const opt = establishmentOptions.find((o) => o.value === id);
      result.push({
        key: `est-${id}`,
        label: `Estabelecimento: ${opt?.label || id}`,
        onRemove: () => onEstablishmentsChange(establishments.filter((e) => e !== id)),
      });
    });

    return result;
  }, [months, defaultMonth, isDefaultPeriod, generators, establishments, generatorOptions, establishmentOptions, onMonthsChange, onGeneratorsChange, onEstablishmentsChange]);

  const handleApply = () => {
    trackButtonClick({ buttonLabel: 'indicadores_ambientais_aplicar_filtro' });
    onMonthsChange(draftMonths);
    onEstablishmentsChange(draftEstablishments);
    onGeneratorsChange(draftGenerators);
    setOpen(false);
  };

  const handleClear = () => {
    onClear();
    setOpen(false);
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
          {chips.map((chip) =>
            chip.onRemove ? (
              <button
                key={chip.key}
                type="button"
                onClick={chip.onRemove}
                className="p-0 border-0 bg-transparent"
                aria-label={`Remover filtro ${chip.label}`}
              >
                <Badge size="small" variant="primary" iconRight={<XCircle size={16} />}>
                  {chip.label}
                </Badge>
              </button>
            ) : (
              <Badge key={chip.key} size="small" variant="primary">
                {chip.label}
              </Badge>
            )
          )}
          {activeCount > 0 && (
            <Button
              variant="link"
              onClick={onClear}
              className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
            >
              Limpar tudo
            </Button>
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          data-testid="indicadores-filtro-abrir"
          onClick={() => {
            trackButtonClick({
              buttonLabel: 'indicadores_ambientais_abrir_filtro',
            });
            setOpen(true);
          }}
          className="shrink-0 ml-auto"
        >
          <SlidersHorizontal className="h-4 w-4 mr-2" />
          Filtros
          {activeCount > 0 && (
            <span className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-background text-[11px] font-semibold leading-none">
              {activeCount}
            </span>
          )}
        </Button>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[320px] p-0 flex flex-col gap-0">
          <SheetHeader className="px-4 pt-4 pb-2 border-b shrink-0 space-y-0">
            <SheetTitle>
              <Text variant="heading" className="text-lg font-semibold text-foreground">
                Filtros
              </Text>
            </SheetTitle>
          </SheetHeader>

          <div className="px-4 pt-3 pb-4 space-y-3 flex-1 overflow-y-auto">
            <div className="space-y-1.5">
              <Label>
                <Text variant="label" size="small" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Período
                </Text>
              </Label>
              <PeriodPicker value={draftMonths} onChange={setDraftMonths} maxMonth={maxMonth} />
            </div>

            <div className="space-y-1.5">
              <Label>
                <Text variant="label" size="small" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Estabelecimento
                </Text>
              </Label>
              <EstablishmentMultiSelect
                options={establishmentOptions}
                value={draftEstablishments}
                onChange={setDraftEstablishments}
                placeholder={
                  draftGenerators.length > 0
                    ? `${availableEstablishments.length} estabelecimento${availableEstablishments.length !== 1 ? 's' : ''} do gerador`
                    : 'Todos os estabelecimentos'
                }
              />
            </div>

            {showGeneratorFilter && (
              <div className="space-y-1.5">
                <Label>
                  <Text variant="label" size="small" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Gerador
                  </Text>
                </Label>
                <MultiSelect
                  options={generatorOptions}
                  value={draftGenerators}
                  onChange={setDraftGenerators}
                  placeholder="Todos os geradores"
                  searchPlaceholder="Buscar gerador..."
                  emptyMessage="Nenhum gerador encontrado."
                />
              </div>
            )}
          </div>

          <SheetFooter className="flex-row items-center justify-between px-4 py-3 border-t shrink-0 gap-2">
            <Button variant="outline" size="sm" onClick={handleClear} className="flex-1">
              Limpar filtros
            </Button>
            <Button
              size="sm"
              data-testid="indicadores-filtro-aplicar"
              onClick={handleApply}
              className="flex-1"
            >
              Aplicar filtros
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
};
